<template>
    <div class="inline-flex flex-row items-center text-gray-700 font-light">
        <Tooltip>
            <template #trigger="tooltipProps">
                <div
                    v-if="snapshot.matches('ReadOnly')"
                    ref="readonlyRef"
                    class="h-7 flex items-center"
                    :class="[
                        Boolean(props.disabledExplanation)
                            ? 'cursor-not-allowed'
                            : 'hover:outline-1 hover:outline-dashed hover:outline-gray-700 focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-gray-700 focus-visible:outline-dashed',
                    ]"
                    v-bind="tooltipProps"
                    :tabindex="0"
                    @keydown="handleReadOnlyKeyDown"
                    @click="
                        if (!props.disabledExplanation) {
                            triggerHideTooltip();
                            send({ type: 'Enter edit mode' });
                        }
                    "
                >
                    <slot name="editable-content" />
                </div>
            </template>

            <template v-if="!Boolean(props.disabledExplanation)" #text>
                {{ $t('inline-text-edit-click-to-edit') }}
            </template>
            <template v-else #text>{{ props.disabledExplanation }}</template>
        </Tooltip>

        <div v-if="snapshot.matches('Editing')" ref="editModeWrapperRef" class="relative isolate">
            <div
                :class="[
                    'absolute rounded-[5px] -top-0.75 h-[calc(100%+6px)] pointer-events-none -z-10 shadow-[0_0_2px_rgba(0,0,0,0.5)]',
                    showUnsavedWarning ? 'animate-flash-red' : 'bg-gray-300',
                ]"
                :style="backgroundColorLeftRightStyle"
            />

            <Button
                class="absolute -top-0.5"
                size="Small"
                :variant="
                    snapshot.matches({ Editing: 'Dirty' }) || snapshot.matches({ Editing: 'SaveFailed' })
                        ? 'Danger-Light'
                        : 'Neutral-Light'
                "
                :icon-description="
                    snapshot.matches({ Editing: 'Dirty' }) || snapshot.matches({ Editing: 'SaveFailed' })
                        ? $t('inline-text-edit-discard-changes')
                        : $t('inline-text-edit-close')
                "
                :style="cancelButtonLeftStyle"
                @click="
                    triggerHideTooltip();
                    send({ type: 'Exit edit mode' });
                "
            >
                <template #icon>
                    <CloseIcon
                        v-if="snapshot.matches({ Editing: 'Clean' }) || snapshot.matches({ Editing: 'Pending' })"
                        class="size-4.5"
                    />
                    <UndoIcon
                        v-if="snapshot.matches({ Editing: 'Dirty' }) || snapshot.matches({ Editing: 'SaveFailed' })"
                        class="size-4.5"
                    />
                </template>
            </Button>

            <label :for="inputId" class="sr-only">{{ props.label }}</label>
            <input
                :id="inputId"
                ref="inputRef"
                type="text"
                :value="snapshot.context.currentValue"
                class="inline-text-edit-input bg-transparent focus:bg-gray-50 border-none p-0 m-0 outline-none focus:ring-1 focus:ring-gray-700 focus:ring-offset-1 focus:rounded-xs h-7"
                :autofocus="true"
                :style="inputStyle"
                @keydown="handleInputKeyDown"
                @input="send({ type: 'Change value', value: ($event.target as HTMLInputElement).value })"
            />

            <Button
                class="absolute -top-0.5"
                size="Small"
                :variant="snapshot.matches({ Editing: 'SaveFailed' }) ? 'Danger-Strong' : 'Neutral-Strong'"
                :disabled-explanation="
                    snapshot.matches({ Editing: 'Clean' }) ? $t('inline-text-edit-no-unsaved-changes') : undefined
                "
                :icon-description="
                    snapshot.matches({ Editing: 'SaveFailed' })
                        ? $t('inline-text-edit-save-failed-retry')
                        : snapshot.matches({ Editing: 'Pending' })
                          ? $t('inline-text-edit-saving')
                          : $t('inline-text-edit-save')
                "
                :status="saveButtonStatus"
                :style="saveButtonLeftStyle"
                @click="handleSaveButtonClick"
            >
                <template #icon>
                    <AllDoneIcon class="size-4.5" />
                </template>
            </Button>
        </div>

        <div :style="distanceBetweenInputAndButtonsStyle">
            <Button
                v-if="snapshot.matches('ReadOnly')"
                :disabled-explanation="props.disabledExplanation"
                size="Small"
                variant="Transparent-Light"
                :icon-description="$t('inline-text-edit-click-to-edit')"
                @click="send({ type: 'Enter edit mode' })"
            >
                <template #icon>
                    <EditIcon class="size-4.5" />
                </template>
            </Button>

            <!-- This div avoids potential layout shifts -->
            <div v-else class="size-8" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { onClickOutside, useElementSize } from '@vueuse/core';
import { useMachine } from '@xstate/vue';
import { computed, provide, ref, useId, watchEffect } from 'vue';
import Button from '../Button/Button.vue';
import AllDoneIcon from '../Icon/AllDoneIcon.vue';
import CloseIcon from '../Icon/CloseIcon.vue';
import EditIcon from '../Icon/EditIcon.vue';
import UndoIcon from '../Icon/UndoIcon.vue';
import { triggerHideTooltipKey } from '../Tooltip/injectionKeys';
import Tooltip from '../Tooltip/Tooltip.vue';
import { createStateMachine } from './InlineTextEdit.state';

type InlineTextEditProps = {
    label: string;
    value: string;
    disabledExplanation?: string;
};

type InlineTextEditEmits = {
    (e: 'save', value: string, onDone: (error?: Error) => void): void;
};

const props = defineProps<InlineTextEditProps>();
const emit = defineEmits<InlineTextEditEmits>();

const stateMachine = computed(() => createStateMachine(props.value, handleSaveChanges));
const { snapshot, send } = useMachine(stateMachine.value);

const id = useId();
const inputId = `input-${id}`;

const readonlyRef = ref(null);
const inputRef = ref<HTMLInputElement | null>(null);
const { width: readonlyWidth } = useElementSize(readonlyRef);
const capturedWidth = ref(0);

const inputStyle = computed(() => {
    return {
        minWidth: `max(${capturedWidth.value}px, 10rem)`,
        '--captured-width': `${capturedWidth.value}px`,
    };
});

const editModeWrapperRef = ref<HTMLElement | null>(null);
const showUnsavedWarning = ref(false);

onClickOutside(editModeWrapperRef, () => {
    if (snapshot.value.matches({ Editing: 'Clean' })) {
        send({ type: 'Exit edit mode' });
    } else if (snapshot.value.matches({ Editing: 'Dirty' }) || snapshot.value.matches({ Editing: 'SaveFailed' })) {
        showUnsavedWarning.value = true;
        setTimeout(() => {
            showUnsavedWarning.value = false;
        }, 400);
    }
});

// Reset warning when changes are no longer dirty
watchEffect(() => {
    if (!snapshot.value.matches({ Editing: 'Dirty' })) {
        showUnsavedWarning.value = false;
    }
});

// Capture the width when entering edit mode or update it in readonly mode
watchEffect(() => {
    if (snapshot.value.matches('ReadOnly') && readonlyWidth.value > 0) {
        capturedWidth.value = readonlyWidth.value;
    }
});

// Focus input when entering edit mode
watchEffect(() => {
    if (snapshot.value.matches('Editing') && inputRef.value) {
        inputRef.value.focus();
    }
});

function handleSaveChanges(value: string): Promise<void> {
    return new Promise((resolve, reject) => {
        emit('save', value, (error?: Error) => {
            if (error) {
                reject(error);
            } else {
                resolve();
            }
        });
    });
}

function handleSaveButtonClick() {
    send({ type: 'Save changes' });
}

function handleReadOnlyKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !props.disabledExplanation) {
        event.preventDefault();
        triggerHideTooltip();
        send({ type: 'Enter edit mode' });
    }
}

function handleInputKeyDown(event: KeyboardEvent) {
    switch (event.key) {
        case 'Enter':
            if (snapshot.value.matches({ Editing: 'Dirty' })) {
                send({ type: 'Save changes' });
                event.preventDefault();
            } else {
                send({ type: 'Exit edit mode' });
                event.preventDefault();
            }
            break;

        case 'Escape': {
            send({ type: 'Exit edit mode' });
            event.preventDefault();
        }
    }
}

const saveButtonStatus = computed(() => {
    if (snapshot.value.matches({ Editing: 'Pending' })) {
        return 'Loading';
    } else if (snapshot.value.matches({ Editing: 'SaveFailed' })) {
        return 'Error';
    }

    return undefined;
});

// This is used to force close the Tooltip after clicking the button
const triggerHideTooltipKeyCounter = ref(0);
provide(triggerHideTooltipKey, triggerHideTooltipKeyCounter);

function triggerHideTooltip() {
    triggerHideTooltipKeyCounter.value = triggerHideTooltipKeyCounter.value + 1;
}

const distanceBetweenInputAndButtonsInPixel = 4;

const distanceBetweenInputAndButtonsStyle = computed(() => {
    return {
        'margin-left': `${distanceBetweenInputAndButtonsInPixel}px`,
    };
});

const cancelButtonLeftStyle = computed(() => {
    const buttonWidthInPixel = 32;

    return {
        left: `-${buttonWidthInPixel + distanceBetweenInputAndButtonsInPixel}px`,
    };
});

const saveButtonLeftStyle = computed(() => {
    return {
        left: `calc(100% + ${distanceBetweenInputAndButtonsInPixel}px)`,
    };
});

const backgroundColorLeftRightStyle = computed(() => {
    const buttonWidthInPixel = 32;
    const buttonWithStatusWidthInPixel = 56;
    const totalDistanceInPixel = buttonWidthInPixel + distanceBetweenInputAndButtonsInPixel + 1;
    const totalDistanceWithStatusInPixel = buttonWithStatusWidthInPixel + distanceBetweenInputAndButtonsInPixel + 1;

    const isLargeRightButton =
        snapshot.value.matches({ Editing: 'Pending' }) || snapshot.value.matches({ Editing: 'SaveFailed' });

    return {
        left: `-${totalDistanceInPixel}px`,
        right: `-${isLargeRightButton ? totalDistanceWithStatusInPixel : totalDistanceInPixel}px`,
    };
});
</script>

<style scoped>
@supports (field-sizing: content) {
    .inline-text-edit-input {
        field-sizing: content;
        max-width: 30rem;
    }
}

@supports not (field-sizing: content) {
    .inline-text-edit-input {
        width: var(--captured-width);
        max-width: 30rem;
    }
}

@keyframes flash-red {
    0% {
        background-color: var(--color-gray-300);
    }
    50% {
        background-color: var(--color-red-500);
        opacity: 0.5;
    }
    100% {
        background-color: var(--color-gray-300);
    }
}

.animate-flash-red {
    animation: flash-red 0.4s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
    .animate-flash-red {
        animation: none;
    }
}
</style>
